import React, { useState, useEffect } from "react";
import {
  KeyRound, Calendar, Bell, ShieldCheck, ShieldAlert,
  CheckCircle, XCircle, Pencil, Trash2, Plug, Activity,
} from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { useAlert } from "context/AlertContext";
import apiSendgridCredential from "./api";
import { getCredentialFields } from "./features/credential.fields";
import { getCredentialValidation } from "./features/credential.validation";
import { loadCredentialDependencies } from "./features/credential.dependencies";

const maskApiKey = (key) => {
  if (!key) return "—";
  return `••••••••${key.slice(-8)}`;
};

const SendgridCredential = ({ onTotalCountChange }) => {
  const { showAlert } = useAlert();

  const [records,       setRecords]       = useState([]);
  const [totalCount,    setTotalCount]    = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [resetTrigger,  setResetTrigger]  = useState(0);
  const [sortConfig,    setSortConfig]    = useState({ key: "id", direction: "desc" });
  const [selectedRows,  setSelectedRows]  = useState([]);
  const [modalId,       setModalId]       = useState(null);
  const [isModalOpen,   setIsModalOpen]   = useState(false);
  const [confirmData,   setConfirmData]   = useState({
    isOpen: false, title: "", message: "", confirmText: "Yes", cancelText: "Cancel", icon: null, resolve: null,
  });

  const isEditing = !!modalId;

  // ── Data Fetching ─────────────────────────────────────────────────────────

  const load = async (page, size, q) => {
    setLoading(true);
    try {
      const res = await apiSendgridCredential.getAll({
        searchText: q.trim(),
        limit: size,
        skip: (page - 1) * size,
      });
      setRecords(res.data?.data || []);
      setTotalCount(res.data?.total_count || 0);
      onTotalCountChange?.(res.data?.total_count || 0);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to load credentials." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1, pageSize, ""); }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = (val) => {
    setSearch(val);
    setCurrentPage(1);
    load(1, pageSize, val);
  };

  const handleRefresh = () => {
    setSearch("");
    setCurrentPage(1);
    setResetTrigger((k) => k + 1);
    load(1, pageSize, "");
  };

  const handlePageChange = (page, newSize = pageSize) => {
    const s = newSize;
    const p = newSize !== pageSize ? 1 : page;
    setCurrentPage(p);
    setPageSize(s);
    load(p, s, search);
  };

  const handleSort = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const handleCreate = () => { setModalId(null); setIsModalOpen(true); };
  const handleEdit   = (id) => { setModalId(id); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setModalId(null); };

  const openConfirm = (opts) =>
    new Promise((resolve) => setConfirmData({ ...opts, isOpen: true, resolve }));

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((p) => ({ ...p, isOpen: false, resolve: null }));
  };

  const handleDelete = async (row) => {
    const confirmed = await openConfirm({
      title:       "Delete Credential",
      message:     "Are you sure you want to delete this SendGrid credential? This action cannot be undone.",
      confirmText: "Delete",
      cancelText:  "Cancel",
      variant:     "danger",
      icon:        <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiSendgridCredential.delete(row.id);
      if (res.data?.status) {
        showAlert({ type: "success", message: "SendGrid credential deleted successfully." });
        load(currentPage, pageSize, search);
      } else {
        showAlert({ type: "warning", message: res.data?.message });
      }
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Delete failed." });
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return;
    const confirmed = await openConfirm({
      title:       "Delete Selected",
      message:     `Delete ${selectedRows.length} credential(s)? This cannot be undone.`,
      confirmText: "Delete All",
      cancelText:  "Cancel",
      variant:     "danger",
      icon:        <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      await Promise.all(selectedRows.map((id) => apiSendgridCredential.delete(id)));
      showAlert({ type: "success", message: `${selectedRows.length} credential(s) deleted successfully.` });
      setSelectedRows([]);
      load(1, pageSize, search);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Bulk delete failed." });
    }
  };

  // ── EntityFormModal wiring ─────────────────────────────────────────────────

  const fetchCredentialById = async (id) => {
    const res = await apiSendgridCredential.getById(id);
    if (res?.data?.status && res.data.data?.plan_expiry_date) {
      res.data.data = {
        ...res.data.data,
        plan_expiry_date: new Date(res.data.data.plan_expiry_date).toISOString().split("T")[0],
      };
    }
    return res;
  };

  // ── Column Definitions ────────────────────────────────────────────────────

  const columns = [
    {
      key:      "id",
      label:    "ID",
      width:    "80px",
      fixed:    true,
      sortable: true,
      render: (row) => (
        <div className="font-mono text-xs font-medium text-text_primary">
          {`#SGC${String(row.id).padStart(3, "0")}`}
        </div>
      ),
    },
    {
      key:   "api_key",
      label: "API Key",
      width: "220px",
      render: (row) => (
        <div className="flex items-center gap-2">
          <KeyRound size={13} className="text-text_muted flex-shrink-0" />
          <span className="font-mono text-xs text-text_secondary tracking-wider">
            {maskApiKey(row.api_key)}
          </span>
        </div>
      ),
    },
    {
      key:      "per_day_limit",
      label:    "Email Limits",
      width:    "120px",
      sortable: true,
      render: (row) => (
        <div className="space-y-1">
          <div className="text-xs text-text_secondary">
            <span className="text-text_muted">Daily: </span>
            <span className="font-medium text-text_primary">
              {row.per_day_limit?.toLocaleString() ?? "—"}
            </span>
          </div>
          <div className="text-xs text-text_secondary">
            <span className="text-text_muted">Total: </span>
            <span className="font-medium text-text_primary">
              {row.total_mail_limit?.toLocaleString() ?? "—"}
            </span>
          </div>
        </div>
      ),
    },
    {
      key:   "sent_today",
      label: "Today's Usage",
      width: "220px",
      render: (row) => {
        const sentToday  = (row.sendgrid_configurations || []).reduce((sum, c) => sum + (c.sent_today || 0), 0);
        const limit      = row.per_day_limit || 0;
        const remaining  = Math.max(0, limit - sentToday);
        const pct        = limit > 0 ? Math.min(100, Math.round((sentToday / limit) * 100)) : 0;
        const isCritical = pct >= 90;
        const isWarning  = pct >= 70 && pct < 90;
        const barColor   = isCritical ? "bg-danger" : isWarning ? "bg-warning" : "bg-success";
        const textColor  = isCritical ? "text-danger" : isWarning ? "text-warning" : "text-success";

        return (
          <div className="space-y-1.5 min-w-[140px]">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1 text-text_muted">
                <Activity size={11} />
                <span>Sent today</span>
              </div>
              <span className={`font-semibold ${textColor}`}>{pct}%</span>
            </div>

            <div className="h-1.5 w-full bg-surface_alt rounded-full overflow-hidden border border-border">
              <div
                className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                style={{ width: `${pct}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-[11px]">
              <span className="text-text_muted">
                {sentToday.toLocaleString()} / {limit.toLocaleString()}
              </span>
              <span className={`font-medium ${remaining === 0 ? "text-danger" : "text-text_secondary"}`}>
                {remaining.toLocaleString()} left
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key:      "plan_expiry_date",
      label:    "Plan Expiry",
      width:    "170px",
      sortable: true,
      render: (row) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiry = row.plan_expiry_date ? new Date(row.plan_expiry_date) : null;
        const isExpired = expiry && expiry <= today;
        const reminderMs = (row.reminder_days_before || 0) * 86400000;
        const isSoon = expiry && !isExpired && reminderMs > 0 && today >= new Date(expiry.getTime() - reminderMs);

        return (
          <div className="space-y-1">
            {expiry ? (
              <div className={`flex items-center gap-1.5 text-xs font-medium ${isExpired ? "text-danger" : isSoon ? "text-warning" : "text-text_primary"}`}>
                <Calendar size={12} className="flex-shrink-0" />
                {expiry.toLocaleDateString()}
                {isExpired && (
                  <span className="px-1.5 py-0.5 bg-danger_bg rounded text-[10px] border border-danger_border">
                    Expired
                  </span>
                )}
                {isSoon && !isExpired && (
                  <span className="px-1.5 py-0.5 bg-warning_bg rounded text-[10px] border border-warning_border">
                    Soon
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs text-text_muted">No expiry set</span>
            )}
            {row.reminder_days_before ? (
              <div className="flex items-center gap-1.5 text-xs text-text_muted">
                <Bell size={11} className="flex-shrink-0" />
                {row.reminder_days_before}d reminder
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key:   "is_domain_verified",
      label: "Domain",
      width: "120px",
      render: (row) =>
        row.is_domain_verified ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
            <ShieldCheck size={12} /> Verified
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-warning_bg text-warning rounded-lg text-xs font-medium border border-warning_border">
            <ShieldAlert size={12} /> Unverified
          </span>
        ),
    },
    {
      key:   "sendgrid_configurations",
      label: "Configs",
      width: "90px",
      render: (row) => {
        const count = row.sendgrid_configurations?.length || 0;
        return (
          <div className="flex items-center gap-1.5">
            <Plug size={12} className="text-text_muted" />
            <span className={`text-xs font-semibold ${count > 0 ? "text-primary" : "text-text_muted"}`}>
              {count}
            </span>
          </div>
        );
      },
    },
    {
      key:   "is_activated",
      label: "Status",
      width: "100px",
      render: (row) =>
        row.is_activated ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
            <CheckCircle size={12} /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-surface_alt text-text_secondary rounded-lg text-xs font-medium border border-border">
            <XCircle size={12} /> Inactive
          </span>
        ),
    },
  ];

  const renderRowActions = (row) => (
    <div className="flex items-center gap-2">
      <button
        onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary_tint text-primary border border-primary/20 hover:bg-primary hover:text-white transition-all duration-200 text-xs font-semibold shadow-sm"
      >
        <Pencil size={13} /> Edit
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-danger_bg text-danger border border-danger_border hover:bg-danger hover:text-white transition-all duration-200 text-xs font-semibold shadow-sm"
      >
        <Trash2 size={13} /> Delete
      </button>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  const activeCount   = records.filter((r) => r.is_activated).length;
  const verifiedCount = records.filter((r) => r.is_domain_verified).length;

  return (
    <div className="h-full overflow-y-auto">
    <div className="space-y-4 pb-4">

      {/* Summary Stats */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1.5 bg-primary_tint text-primary rounded-lg text-xs font-medium border border-primary/20">
          Total: {totalCount}
        </span>
        <span className="px-3 py-1.5 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
          Active: {activeCount}
        </span>
        <span className="px-3 py-1.5 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
          Domain Verified: {verifiedCount}
        </span>
      </div>

      {/* Toolbar */}
      <TableToolBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onCreate={handleCreate}
        onDeleteSelected={handleBulkDelete}
        selectedCount={selectedRows.length}
        resetSearchTrigger={resetTrigger}
        canCreate
      />

      {/* Table / Empty State */}
      {!loading && records.length === 0 ? (
        <NoDataState
          icon="file"
          title="No SendGrid Credentials"
          message={
            search
              ? "No credentials match your search. Try adjusting the search terms."
              : "Add your first SendGrid API credential to start sending emails."
          }
          showRefresh
          onRefresh={handleRefresh}
          showCreate={true}
          createText="Create New Sendgrid Credential"
          onCreate={handleCreate}
          size="medium"
        />
      ) : (
        <>
          <div className="overflow-hidden border rounded-lg border-border">
            <TableGrid
              data={records}
              loading={loading}
              columns={columns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onSelectionChange={setSelectedRows}
              showActions
              selectable
              renderActions={renderRowActions}
            />
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            selectedCount={selectedRows.length}
          />
        </>
      )}

      {/* Create / Edit Modal */}
      <EntityFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        id={modalId}
        title="SendGrid Credential"
        fetchById={fetchCredentialById}
        createFn={(data) => apiSendgridCredential.create(data)}
        updateFn={(id, data) => apiSendgridCredential.update(id, data)}
        dependenciesLoader={loadCredentialDependencies}
        fields={getCredentialFields(isEditing)}
        validationSchema={getCredentialValidation(isEditing)}
        onSuccess={() => load(currentPage, pageSize, search)}
      />

      {/* Confirm Dialog */}
      {confirmData.isOpen && (
        <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
      )}
    </div>
    </div>
  );
};

export default SendgridCredential;
